import { plural, t } from "@lingui/macro";
import {
  IApiEpBillingBill,
  IApiEpConfigBillingPlan,
  IApiSmpMedium,
  IPeriodUnit,
} from "@smp/api";
import { IApiEpBillingFeature } from "@smp/api/build/ep/billing/feature/feature.types";
import { useCallback } from "react";

import { EAiCardTag } from "@/screens/app/components/ai_assistant/fixtures/cards.types";

export const useHumanizer = (): {
  humanize: (text: string) => string;
  humanizeMediumAccount: (medium: IApiSmpMedium, simplify?: boolean) => string;
  humanizeBillStatus: (status: IApiEpBillingBill["status"]) => string;
  humanizeBillingGroupDescriptionById: (
    groupId: IApiEpConfigBillingPlan["group"]["id"],
  ) => string;
  humanizeBillingGroupId: (
    groupId: IApiEpConfigBillingPlan["group"]["id"],
  ) => string;
  humanizeBillingPlanDescriptionById: (
    planId: IApiEpConfigBillingPlan["id"],
  ) => string;
  humanizeBillingPlanId: (planId: IApiEpConfigBillingPlan["id"]) => string;
  humanizeFeatureName: (featureName: IApiEpBillingFeature["name"]) => string;
  humanizePeriodUnit: (
    periodUnit: IPeriodUnit,
    value?: number,
    hideValue?: boolean,
  ) => string;
  humanizeAiCardTag: (tag: any) => string;
} => {
  const humanize = useCallback(
    (text: string) =>
      ((
        {
          Activity: t`Activity`,
          "Frequently used": t`Frequently used`,
          "Animals & Nature": t`Animals & Nature`,
          "Emojis & People": t`Emojis & People`,
          Flags: t`Flags`,
          "Food & Drink": t`Food & Drink`,
          Objects: t`Objects`,
          "Smileys & People": t`Smileys & People`,
          Symbols: t`Symbols`,
          "Travel & Places": t`Travel & Places`,
          avito: t`Avito`,
          discord: t`Discord`,
          elama: t`eLama`,
          facebook: t`Facebook`,
          instagram: t`Instagram `,
          instagram_business: t`Instagram Business`,
          kazan_express: t`KazanExpress`,
          linkedin: t`LinkedIn`,
          ok: t`Odnoklassniki`,
          ozon: t`Ozon`,
          pinterest: t`Pinterest`,
          sber_market: t`Sbermegamarket`,
          telegram: t`Telegram`,
          telegram_mtproto: t`Telegram Stories`,
          twitter: t`Twitter`,
          viber: t`Viber`,
          vk: t`VK`,
          vk_ads: t`VK Ads`,
          tenchat: "TenChat",
          wildberries: t`Wildberries`,
          yandex_market: t`Yandex.Market`,
          youtube: t`YouTube`,
          tiktok: "TikTok",
        } as Record<string, string>
      )[text] || text),
    [],
  );

  const humanizeBillStatus = useCallback(
    (status: IApiEpBillingBill["status"]) =>
      ({
        CANCELLED: t`Canceled`,
        ERROR: t`Error`,
        NEW: t`New`,
        PAID: t`Paid`,
        PROCESSING: t`Processing`,
        REFUNDED: t`Refunded`,
      }[status]),
    [],
  );

  const humanizeBillingGroupDescriptionById = useCallback(
    (groupId: IApiEpConfigBillingPlan["group"]["id"]) =>
      ({
        canvas: t`Canvas without watermark`,
        free: t`Suitable for beginner SMM specialists`,
        free_foreign: t`Suitable for beginner SMM specialists`,
        l_business: t`Suitable for corporations and larger community networks`,
        m_business: t`Suitable for bloggers, influencers, and entrepreneurs`,
        pro: t`Suitable for SMM professionals`,
        s_business: t`Suitable for small/one-man businesses`,
        starter: t`Suitable for beginner SMM specialists`,
      }[groupId]),
    [],
  );

  const humanizeBillingGroupId = useCallback(
    (groupId: IApiEpConfigBillingPlan["group"]["id"]) =>
      ({
        canvas: t`Canvas`,
        free: t`Trial`,
        free_foreign: t`Free`,
        l_business: t`Large Business`,
        m_business: t`Medium Business`,
        pro: t`Pro`,
        s_business: t`Small Business`,
        starter: t`Starter`,
      }[groupId]),
    [],
  );

  const humanizeBillingPlanDescriptionById = useCallback(
    (planId: IApiEpConfigBillingPlan["id"]) =>
      ({
        canvas_2023: t`Canvas without watermark`,
        elama_2023: "",
        free: t`Suitable for beginner SMM specialists`,
        free_2022: t`Suitable for beginner SMM specialists`,
        free_foreign: t`Suitable for beginner SMM specialists`,
        l_business200_2016: t`Suitable for SMM professionals`,
        l_business250_2021: t`Suitable for SMM professionals`,
        l_business500_2019: t`Suitable for SMM professionals`,
        l_business_2015: t`Large Business`,
        m_business100_2018: t`Medium Business 100`,
        m_business125_2018: t`Medium Business 125`,
        m_business75_2018: t`Medium Business 75`,
        m_business_2015: t`Medium Business`,
        pro15_2018: t`Suitable for SMM professionals`,
        pro20_2019: t`Suitable for SMM professionals`,
        pro_2015: t`Suitable for SMM professionals`,
        pro_2015_trial: t`Suitable for SMM professionals`,
        s_business35_2018: t`Suitable for SMM professionals`,
        s_business40_2019: t`Suitable for SMM professionals`,
        s_business_2015: t`Suitable for SMM professionals`,
        starter_2015: t`Suitable for beginner SMM specialists`,
        starter_2015_trial: t`Suitable for beginner SMM specialists`,
        starter_2015_trial_classic: t`Suitable for beginner SMM specialists`,
      }[planId]),
    [],
  );

  const humanizeBillingPlanId = useCallback(
    (planId: IApiEpConfigBillingPlan["id"]) =>
      ({
        canvas_2023: t`Canvas`,
        elama_2023: t`eLama`,
        free: t`Free Trial`,
        free_2022: t`Free`,
        free_foreign: t`Free`,
        l_business200_2016: t`Business` + " 200",
        l_business250_2021: t`Business` + " 250",
        l_business500_2019: t`Business` + " 500",
        l_business_2015: t`Business` + " 150",
        m_business100_2018: t`Business` + " 100",
        m_business125_2018: t`Business` + " 125",
        m_business75_2018: t`Business` + " 75",
        m_business_2015: t`Business` + " 50",
        pro15_2018: t`Pro` + "15",
        pro20_2019: t`Pro` + "20",
        pro_2015: t`Pro`,
        pro_2015_trial: t`Pro (trial)`,
        s_business35_2018: t`Business` + " 35",
        s_business40_2019: t`Business` + " 40",
        s_business_2015: t`Business`,
        starter_2015: t`Starter`,
        starter_2015_trial: t`Starter (trial)`,
        starter_2015_trial_classic: t`Starter (trial)`,
      }[planId]),
    [],
  );

  const humanizeFeatureName = useCallback(
    (featureName: IApiEpBillingFeature["name"]) =>
      ({
        own_proxy: t`Personal proxy servers`,
        video_posting: t`Post videos`,
        watermark_slot: t`Additional watermark slot`,
      }[featureName]),
    [],
  );

  const humanizeMediumAccount = useCallback(
    (medium, simplify?: boolean) =>
      ({
        bitly: "bitly",
        discord: t`Discord`,
        elama: t`eLama`,
        facebook: t`Facebook`,
        google: "Google",
        instagram: simplify ? t`Instagram` : t`Instagram Stories (unofficial)`,
        instagram_business: t`Instagram`,
        linkedin: t`LinkedIn`,
        mytarget: "myTarget",
        ok: t`Odnoklassniki`,
        periscope: "Periscope",
        pinterest: t`Pinterest`,
        pushbullet: "Pushbullet",
        rss: "RSS",
        slack: "Slack",
        smmplanner: "SMMplanner",
        snapchat: "Snapchat",
        telegram: t`Telegram`,
        tenchat: "TenChat",
        tiktok: "TikTok",
        tumblr: "Tumblr",
        twitter: t`Twitter`,
        viber: t`Viber`,
        vk: t`VK`,
        vk_ads: t`VK`,
        whatsapp: "WhatsApp",
        yandex: t`Yandex`,
        youtube: t`YouTube`,
        max: "MAX",
        telegram_mtproto: simplify ? t`Telegram` : t`Telegram Stories`,
      }[medium]),
    [],
  );

  const humanizePeriodUnit = useCallback(
    (periodUnit: IPeriodUnit, value?: number, hideValue?: boolean) => {
      if (value === undefined) {
        return "";
      }

      return {
        [IPeriodUnit.NONE]: "",
        [IPeriodUnit.MILLISECOND]: !hideValue
          ? plural(value, {
              zero: "# milliseconds",
              one: "# millisecond",
              few: "# milliseconds",
              many: "# milliseconds",
              other: "# milliseconds",
            })
          : plural(value, {
              zero: "milliseconds",
              one: "millisecond",
              few: "milliseconds",
              many: "milliseconds",
              other: "milliseconds",
            }),
        [IPeriodUnit.SECOND]: !hideValue
          ? plural(value, {
              zero: "# seconds",
              one: "# second",
              few: "# seconds",
              many: "# seconds",
              other: "# seconds",
            })
          : plural(value, {
              zero: "seconds",
              one: "second",
              few: "seconds",
              many: "seconds",
              other: "seconds",
            }),
        [IPeriodUnit.MINUTE]: !hideValue
          ? plural(value, {
              zero: "# minutes",
              one: "# minute",
              few: "# minutes",
              many: "# minutes",
              other: "# minutes",
            })
          : plural(value, {
              zero: "minutes",
              one: "minute",
              few: "minutes",
              many: "minutes",
              other: "minutes",
            }),
        [IPeriodUnit.HOUR]: !hideValue
          ? plural(value, {
              zero: "# hours",
              one: "# hour",
              few: "# hours",
              many: "# hours",
              other: "# hours",
            })
          : plural(value, {
              zero: "hours",
              one: "hour",
              few: "hours",
              many: "hours",
              other: "hours",
            }),
        [IPeriodUnit.DAY]: !hideValue
          ? plural(value, {
              zero: "# days",
              one: "# day",
              few: "# days",
              many: "# days",
              other: "# days",
            })
          : plural(value, {
              zero: "days",
              one: "day",
              few: "days",
              many: "days",
              other: "days",
            }),
        [IPeriodUnit.WEEK]: !hideValue
          ? plural(value, {
              zero: "# weeks",
              one: "# week",
              few: "# weeks",
              many: "# weeks",
              other: "# weeks",
            })
          : plural(value, {
              zero: "weeks",
              one: "week",
              few: "weeks",
              many: "weeks",
              other: "weeks",
            }),
        [IPeriodUnit.MONTH]: !hideValue
          ? plural(value, {
              zero: "# months",
              one: "# month",
              few: "# months",
              many: "# months",
              other: "# months",
            })
          : plural(value, {
              zero: "months",
              one: "month",
              few: "months",
              many: "months",
              other: "months",
            }),
        [IPeriodUnit.YEAR]: !hideValue
          ? plural(value, {
              few: "# years",
              many: "# years",
              one: "# year",
              other: "# years",
              zero: "# years",
            })
          : plural(value, {
              few: "years",
              many: "years",
              one: "year",
              other: "years",
              zero: "years",
            }),
      }[periodUnit];
    },
    [],
  );

  const humanizeAiCardTag = useCallback(
    (tag: EAiCardTag) =>
      ({
        [EAiCardTag.TEXT]: t`__assistant.tag.text`,
        [EAiCardTag.IDEA]: t`__assistant.tag.idea`,
        [EAiCardTag.POST]: t`__assistant.tag.post`,
        [EAiCardTag.STORY]: t`__assistant.tag.story`,
        [EAiCardTag.IMAGE]: t`__assistant.tag.image`,
        [EAiCardTag.PLANNING]: t`__assistant.tag.planning`,
        [EAiCardTag.VIDEO]: t`__assistant.tag.video`,
        [EAiCardTag.TOOL]: t`__assistant.tag.tool`,
      }[tag]),
    [],
  );

  return {
    humanize,
    humanizeBillStatus,
    humanizeBillingGroupDescriptionById,
    humanizeBillingGroupId,
    humanizeBillingPlanDescriptionById,
    humanizeBillingPlanId,
    humanizeFeatureName,
    humanizeMediumAccount,
    humanizePeriodUnit,
    humanizeAiCardTag,
  };
};
